import { faUserPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo } from 'react';

import type { UserMission } from '@/types/userMission';
import { Pill, Typography } from '@/ui';
import MissionProgressBar from '@/ui/components/MissionProgressBar';
import type { MissionProgressTone } from '@/ui/components/MissionProgressBar/types';
import { cn } from '@/ui/lib/utils';
import { formatMissionReward, formatShortDate, missionLastDay } from '@/utils/userMissions';

import './styles.css';
import type { Props } from './types';

type Status = UserMission['status'];

// Only shown when it isn't the default; current missions are mostly Started.
const STATUS_COLORS = {
  Finished: 'warning',
  Claimed: 'primary',
  Expired: 'neutral'
} as const;

// The mobile app's mission card colors (Colors.blue_500 / purple_500). Dark text,
// not the app's white, keeps the small pill readable on these light fills.
const RECURRENCY_PILL_CLASSES: Record<NonNullable<UserMission['recurrency']>, string> = {
  Daily:
    'bg-uniform-blue-500 text-uniform-gray-900 dark:bg-uniform-blue-500 dark:text-uniform-gray-900',
  Weekly:
    'bg-uniform-purple-500 text-uniform-gray-900 dark:bg-uniform-purple-500 dark:text-uniform-gray-900'
};

const PROGRESS_TONES: Record<Status, MissionProgressTone> = {
  Started: 'active',
  Finished: 'complete',
  Claimed: 'complete',
  Expired: 'inactive'
};

const UserMissionRow = ({ userMission, memberTimeZone, showRecurrency = true }: Props) => {
  const { status } = userMission;
  const reward = formatMissionReward(userMission.payout_totals);
  const startedAt = new Date(userMission.created_at);
  const lastDay = userMission.expired_at ? missionLastDay(userMission.expired_at) : null;
  // Dates are the member's, since the mission window is their day or week.
  const startLabel = formatShortDate(startedAt, memberTimeZone);
  const endLabel = lastDay ? formatShortDate(lastDay, memberTimeZone) : null;

  return (
    <li
      className="grid grid-cols-[1fr_auto] items-center gap-4 px-5 py-4"
      data-testid={`user-mission-${userMission.id}`}>
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Typography variant="subheading" as="span">
            {userMission.title}
          </Typography>
          {showRecurrency && userMission.recurrency && (
            <Pill size="sm" className={RECURRENCY_PILL_CLASSES[userMission.recurrency]}>
              {userMission.recurrency}
            </Pill>
          )}
          {status !== 'Started' && (
            <Pill size="sm" color={STATUS_COLORS[status]}>
              {status}
            </Pill>
          )}
        </div>

        {userMission.description && (
          <Typography muted className="line-clamp-2">
            {userMission.description}
          </Typography>
        )}

        {userMission.goal !== null ? (
          <MissionProgressBar
            score={userMission.score}
            goal={userMission.goal}
            tone={PROGRESS_TONES[status]}
            label={userMission.title}
          />
        ) : (
          <Typography variant="caption" muted>
            Score {userMission.score}
          </Typography>
        )}

        <Typography variant="caption" muted as="p">
          {userMission.assigned_by ? (
            <span>
              <FontAwesomeIcon icon={faUserPen} className="mr-1" />
              Assigned by {userMission.assigned_by.name || userMission.assigned_by.email}
            </span>
          ) : (
            'Assigned automatically'
          )}
          {' · '}
          {startLabel}
          {/* Daily missions start and end on one day, so show it once. */}
          {endLabel && endLabel !== startLabel && ` – ${endLabel}`}
          {userMission.claimed_at &&
            ` · Claimed ${formatShortDate(new Date(userMission.claimed_at), memberTimeZone)}`}
        </Typography>
      </div>

      {reward && (
        <div className="text-right" data-testid="mission-reward">
          <Typography
            weight="semibold"
            className={cn(
              'tabular-nums',
              status === 'Expired' &&
                'text-uniform-gray-500 line-through dark:text-uniform-gray-500'
            )}>
            {reward}
          </Typography>
          <Typography variant="caption" muted>
            {status === 'Claimed' ? 'paid' : 'reward'}
          </Typography>
        </div>
      )}
    </li>
  );
};

export default memo(UserMissionRow);
